import EditProfileForm from '@/components/EditProfileForm';
import LocaleSettings from '@/components/LocaleSettings';
import ThemeToggle from '@/components/ThemeToggle';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonPage,
  IonRouterOutlet,
  IonSplitPane,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { t } from 'i18next';
import { createOutline, languageOutline, moonOutline, notificationsOutline, personOutline } from 'ionicons/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Route, useLocation } from 'react-router-dom';
import NotificationsSettings from './settings/NotificationsSettings';
import AccountSettings from './settings/AccountSettings';

const Display = () => (
  <>
    <IonHeader>
      <IonToolbar className="md:hidden">
        <IonButtons slot="start">
          <IonBackButton text={t('back')} defaultHref="/settings" />
        </IonButtons>
        <IonTitle>{t('display')}</IonTitle>
      </IonToolbar>
    </IonHeader>
    <IonContent>
      <ThemeToggle />
    </IonContent>
  </>
);

// Sidebar menu items
const menuItems = [
  { title: 'edit_profile', path: '/settings/edit-profile', iosIcon: createOutline, mdIcon: createOutline, color: 'green' },
  { title: 'display', path: '/settings/display', iosIcon: moonOutline, mdIcon: moonOutline, color: 'blue' },
  { title: 'notifications', path: '/settings/notifications', iosIcon: notificationsOutline, mdIcon: notificationsOutline, color: 'blue' },
  { title: 'languages', path: '/settings/languages', iosIcon: languageOutline, mdIcon: languageOutline, color: 'gray' },
  { title: 'my_account', path: '/settings/account', iosIcon: personOutline, mdIcon: personOutline, color: 'gray' },
];

interface MenuItemProps {
  item: {
    title: string;
    path: string;
    iosIcon: string;
    mdIcon: string;
    color: string;
  };
  isLast: boolean;
}

// MenuItem component to handle active highlighting
const MenuItem: React.FC<MenuItemProps> = ({ item, isLast }) => {
  const { t } = useTranslation();

  const location = useLocation();
  const isActive = location.pathname === item.path;
  return (
    <IonMenuToggle autoHide={false}>
      <IonItem
        routerLink={item.path}
        routerDirection="forward"
        lines={isLast ? 'none' : 'inset'}
        detail={true}
        className={`menu-item ${isActive ? 'active' : ''}`} // Add 'active' class if the route is active
      >
        <div slot="start" className={`flex items-center justify-center size-8 rounded-md bg-${item.color}-500`}>
          <IonIcon slot="start" className={`size-5 text-white`} ios={item.iosIcon} md={item.mdIcon}></IonIcon>
        </div>
        <IonLabel>{t(item.title)}</IonLabel>
      </IonItem>
    </IonMenuToggle>
  );
};

// Menu Content
const SettingsMenu = () => (
  <IonMenu className='hidden md:inline-block' contentId="main" maxEdgeStart={0}>
    <IonContent>
      <IonList inset={true}>
        {menuItems.map((item, index) => (
          <MenuItem key={index} item={item} isLast={true} />
        ))}
      </IonList>
    </IonContent>
  </IonMenu>
);

// Settings Page
const SettingsPage = () => {
  const location = useLocation(); // Get the current route
  const { t } = useTranslation();
  document.title = t('settings');

  return (
    <IonPage>
      <IonContent>
        <IonSplitPane when="xs" contentId="main">
          {/* Sidebar Menu */}
          <SettingsMenu />
          {/* Main Content */}
          <IonContent>
            <IonRouterOutlet key={location.pathname} className='mx-auto bg-secondary dark:bg-background'>
              {/* Default route to render the menu on mobile */}
              <Route exact path="/settings">
                <IonHeader collapse='fade' className='ion-no-border'>
                  <IonToolbar className="md:hidden">
                    <IonButtons slot="start">
                      <IonBackButton text={t('back')} color="" className="text-foreground" defaultHref="/" />
                    </IonButtons>
                  </IonToolbar>
                </IonHeader>
                <h2 className="ion-padding-start text-3xl font-semibold">{t('settings')}</h2>

                <IonList className="rounded-lg m-2 bg-card">
                  {menuItems.map((item, index) => (
                    <MenuItem key={index} item={item} />
                  ))}
                </IonList>
              </Route>

              {/* Other Settings Routes */}
              <Route exact path="/settings/edit-profile" component={EditProfileForm} />
              <Route exact path="/settings/display" component={Display} />
              <Route exact path="/settings/notifications" component={NotificationsSettings} />
              <Route exact path="/settings/account" component={AccountSettings} />
              <Route path="/settings/languages" component={LocaleSettings} />
              {/* Redirect to /settings if no match */}
            </IonRouterOutlet>
          </IonContent>
        </IonSplitPane>
      </IonContent>
    </IonPage>
  );
};

export default SettingsPage;